import { ElementRef, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Util } from '@common/utils/util';
import { environment } from '@src/environments/environment';
import { Observable } from 'rxjs';

import { AlertModalComponent } from './alert/alert';
import { ConfirmationData, ConfirmationModalComponent } from './confirmation/confirmation';
import { ErrorModalComponent } from './error/error';
import { FatalModalComponent } from './fatal/fatal';
import { ModalComponent } from './modal/modal';

export type AutoFocusType = 'YesButton' | 'NoButton';

export type ModalRefResult = {
    result: Observable<string>;
};

export enum ModalResponseCodes {
    OK = 'Ok',
    CANCEL = 'Cancel',
    DISCARD = 'Discard',
    RESTART = 'Restart',
}

export interface ModalData {
    title: string | ElementRef;
    content: string | ElementRef | ElementRef;
    yes: string | ElementRef | ElementRef;
    no: string | ElementRef | ElementRef;
}

export interface ModalErrorData {
    title?: string;
    message: string;
    detailed?: string;
}

export interface ModalAlertData {
    title?: string | ElementRef;
    content?: string;
    ok?: string;
}

@Injectable({ providedIn: 'root' })
export class ModalService {
    public showWarningErrors: boolean = false;

    constructor(public dialog: MatDialog) {
        this.showWarningErrors = environment.show_warnings;
    }

    public openModal(
        title: string | ElementRef,
        content: string | ElementRef | ElementRef,
        yesString: string | ElementRef | ElementRef,
        noString: string | ElementRef | ElementRef,
        autofocus?: AutoFocusType
    ): ModalRefResult {
        autofocus = autofocus || 'YesButton';
        const autoFocus: string = autofocus === 'NoButton' ? '.st-modal-button-no' : '.st-modal-button-yes';
        // TODO: Type Data object better
        const dialogRef = this.dialog.open<ModalComponent, ModalData, string>(ModalComponent, {
            data: {
                title,
                content,
                yes: yesString,
                no: noString,
            },
            hasBackdrop: true,
            delayFocusTrap: true,
            autoFocus,
            restoreFocus: true,
        });
        return {
            result: dialogRef.afterClosed(),
        };
    }

    public openServiceErrorModal(modalInfo: { title?: string; message: string; detailedMessage?: string }): ModalRefResult {
        if (!this.showWarningErrors) return;
        if (!modalInfo.title) {
            modalInfo.title = 'Warning';
        }
        // TODO: Type Data object better
        const dialogRef = this.dialog.open<ErrorModalComponent, ModalErrorData, string>(ErrorModalComponent, {
            data: {
                title: modalInfo.title,
                message: modalInfo.message,
                detailed: modalInfo.detailedMessage,
            },
            hasBackdrop: true,
            delayFocusTrap: true,
            autoFocus: '.st-modal-button-continue',
        });
        Util.saveWarningError(modalInfo.detailedMessage || modalInfo.message);
        return {
            result: dialogRef.afterClosed(),
        };
    }

    public openServiceFatalErrorModal(modalInfo: { title?: string; message: string; detailedMessage: string }, runtime: boolean = false): ModalRefResult {
        if (!modalInfo.title) {
            modalInfo.title = 'Error';
        }
        // TODO: Type Data object better
        const dialogRef = this.dialog.open<FatalModalComponent, ModalErrorData, string>(FatalModalComponent, {
            data: {
                title: modalInfo.title,
                message: modalInfo.message,
                detailed: modalInfo.detailedMessage,
            },
            hasBackdrop: true,
            delayFocusTrap: true,
            autoFocus: '.st-modal-button-reset',
        });
        Util.saveCriticalError(modalInfo.detailedMessage, runtime);
        return {
            result: dialogRef.afterClosed(),
        };
    }

    public openAlert(title?: string | ElementRef, content?: string, okString?: string): ModalRefResult {
        okString = okString || 'OK';
        // TODO: Type Data object better
        const dialogRef = this.dialog.open<AlertModalComponent, ModalAlertData, string>(AlertModalComponent, {
            data: {
                title,
                content,
                ok: okString,
            },
            hasBackdrop: true,
            delayFocusTrap: true,
            autoFocus: '.st-modal-button-ok',
        });
        return {
            result: dialogRef.afterClosed(),
        };
    }

    public openConfirmation(data: ConfirmationData): ModalRefResult {
        const dialogRef = this.dialog.open(ConfirmationModalComponent, {
            data,
            hasBackdrop: true,
            delayFocusTrap: true,
            autoFocus: '.st-confirmation-button-yes',
        });

        return {
            result: dialogRef.afterClosed(),
        };
    }

    public beforeDissmis() {
        return false;
    }
}
